//@ts-check
/// <reference path="./table-sorter.d.ts" />
/// <reference path="@types/global.d.ts" />

/**
 * @typedef {import("./table-sorter").TableSorterDirection} TableSorterDirection
 * @typedef {import("./table-sorter").TableSorterOperation} TableSorterOperation
 * @typedef {import("./table-sorter").TableSorterState} TableSorterState
 * @typedef {import("./table-sorter").TableSorterType} TableSorterType
 * @typedef {import("./table-sorter").TableSorterSettingsButton} TableSorterSettingsButton
 * @typedef {import("./table-sorter").TableSorterSettings} TableSorterSettings
 */


export class TableSorter {
	//#region Constants
	
	
	/** @type {string} Attribute to define that a column is sortable */
	static get SORT_ATTR() { return 'data-sort'; }
	
	
	/** @type {string} Attribute for indicating that the element is used for sorting */
	static get SORTED_ATTR() { return 'data-sorted'; }
	
	
	/** @type {string} Attribute used for sorting a specific index */
	static get SORT_INDEX_ATTR() { return 'data-sort-index'; }
	
	
	/** @type {string} Attribute used for setting the value used for the sorting */
	static get SORT_VALUE_ATTR() { return 'data-sort-value'; }
	
	
	
	/** @type {TableSorterState} */
	static get SORT_STATE_ASC() { return 'asc'; }
	
	/** @type {TableSorterState} */
	static get SORT_STATE_DESC() { return 'desc'; }
	
	/** @type {TableSorterState} */
	static get SORT_STATE_NONE() { return 'none'; }
	
	/** @type {TableSorterState} */
	static get SORT_STATE_RE_SORT() { return 're-sort'; }
	
	/** @type {TableSorterState} */
	static get SORT_STATE_DESTROYED() { return 'destroyed'; }
	
	
	
	/** @type {TableSorterDirection & 'asc'} */
	static get SORT_ASC() { return 'asc'; }
	
	/** @type {TableSorterDirection & 'desc'} */
	static get SORT_DESC() { return 'desc'; }
	
	/** @type {TableSorterOperation & 'none'} */
	static get SORT_NONE() { return 'none'; }
	
	
	/** @type {Array<TableSorterOperation|TableSorterState>} All valid types for the `SORT_ATTR` attribute */
	static #SORT_VALID_VALUES = [TableSorter.SORT_ASC, TableSorter.SORT_DESC, TableSorter.SORT_NONE];
	
	static get SORT_VALID_VALUES() { return this.#SORT_VALID_VALUES; }
	
	
	
	/** @type {TableSorterType & 'number'} */
	static get SORT_TYPE_NUMBER() { return 'number'; }
	
	/** @type {TableSorterType & 'natural'} */
	static get SORT_TYPE_NATURAL() { return 'natural'; }
	
	/** @type {TableSorterType & 'text'} */
	static get SORT_TYPE_TEXT() { return 'text'; }
	
	
	/** @type {Array<TableSorterType>} All valid types for the `SORT_ATTR` attribute */
	static #SORT_ATTR_VALID_VALUES = [TableSorter.SORT_TYPE_NUMBER, TableSorter.SORT_TYPE_NATURAL, TableSorter.SORT_TYPE_TEXT];
	
	static get SORT_ATTR_VALID_VALUES() { return this.#SORT_ATTR_VALID_VALUES; }
	
	
	
	/**
	 * All valid types for the `SORT_ATTR` attribute - always sort in ascending order
	 * 
	 * @type {{[key in TableSorterType]: (value_a: string, value_b: string) => number}}
	 */
	static #SORT_FUNCTIONS = {
		[TableSorter.SORT_TYPE_NUMBER]: (value_a, value_b) => {
			const float_a = TableSorter.parseNumber(value_a);
			const float_b = TableSorter.parseNumber(value_b);
			
			/**
			 * We need to send all NaN to the bottom.
			 * To do so, we need to send 1 or -1, depending on which is NaN.
			 * If both are NaN, then we must send 0, or this will never finish sorting
			 */
			if(Number.isNaN(float_a)) {
				if(Number.isNaN(float_b)) {
					return 0;
				}
				
				return 1;
			} else if(Number.isNaN(float_b)) {
				return -1;
			}
			
			// While the raw value should work, it's best to be careful as stability isn't guaranteed
			return Math.sign(float_a - float_b);
		},
		
		[TableSorter.SORT_TYPE_TEXT]: (value_a, value_b) => value_a.localeCompare(value_b),
		
		// https://stackoverflow.com/questions/2802341/natural-sort-of-alphanumerical-strings-in-javascript
		[TableSorter.SORT_TYPE_NATURAL]: (value_a, value_b) => value_a.localeCompare(value_b, undefined, {numeric: true, sensitivity: 'base'}),
	};
	
	static get SORT_FUNCTIONS() { return this.#SORT_FUNCTIONS; }
	
	
	
	/**
	 * Basic sorting function - sorts by the original order
	 * 
	 * @param {HTMLTableRowElement} tr_a
	 * @param {HTMLTableRowElement} tr_b
	 * 
	 * @returns {number} If the row doesn't have an original position, returns 0
	 */
	#SORT_FN_ORIGINAL(tr_a, tr_b) {
		if(!this.#rows_original_pos) {
			return 0;
		}
		
		const index_a = this.#rows_original_pos.get(tr_a);
		const index_b = this.#rows_original_pos.get(tr_b);
		
		if(index_a === undefined || index_b === undefined) {
			return 0;
		}
		
		return index_a - index_b;
	} 
	
	
	/** @type {Required<TableSorterSettings>} Default settings */
	static get DEFAULT_SETTINGS() {
		return {
			columns: [],
			button: {
				tag: 'button',
				className: null,
				title: 'Click here to sort',
				text: {
					[TableSorter.SORT_ASC]: '🔼',
					[TableSorter.SORT_DESC]: '🔽',
					[TableSorter.SORT_NONE]: '↕️'
				},
			},
			autoSort: false,
			defaultSortType: TableSorter.SORT_TYPE_TEXT,
			replacementSortType: null,
			operations: [
				TableSorter.SORT_ASC,
				TableSorter.SORT_DESC,
				TableSorter.SORT_NONE,
			],
			customGetValueFn: null,
			customSortFn: null,
		};
	}
	
	
	//#endregion
	
	
	
	//#region Variables
	
	
	/** @type {TableSorterSettings} The options */
	#options;
	
	/** @type {TableSorterState} The current sort status */
	#sort_status = TableSorter.SORT_STATE_NONE;
	
	/** @type {Set<HTMLTableRowElement>} All the rows in the table header */
	#header_rows = new Set();
	
	/** @type {HTMLTableElement} The table we will act upon */
	#table;
	
	/** @type {HTMLTableSectionElement} `<tbody>` element */
	#tbody;
	
	/** @type {Array<HTMLTableCellElement>} `<td>` in `<thead>` or `<th>` */
	#columns = [];
	
	/** @type {Map<HTMLTableCellElement, Element>} Buttons created for the headers */
	#column_buttons = new Map();
	
	/** @type {Array<HTMLTableRowElement>} Rows to be sorted */
	#rows = [];
	
	/** @type {Map<HTMLTableRowElement, number>} Original position of all rows */
	#rows_original_pos = new Map();
	
	/** @type {Map<Element, EventListenerOrEventListenerObject>} Maps a click handler to the respective element */
	#click_handlers = new Map();
	
	/** @type {HTMLTableCellElement|null} The column that is currently being sorted */
	#sort_column = null;
	
	
	/** @type {number} The index of the current sort operation set in `TableSorterSettings.operations` */
	#sort_op_index = 0;
	
	
	//#endregion
	
	
	
	//#region Initialization
	
	
	/**
	 * @param {HTMLTableElement} table
	 * @param {Partial<TableSorterSettings>} [options={}] Optional settings
	 * 
	 * @throws {TypeError} Argument `table` *must* be a `<table>`
	 * @throws {TypeError} Invalid table - `<tbody>` required
	 * @throws {TypeError} Invalid table - no columns found
	 */
	constructor(table, options = {}) {
		if(!table || !(table instanceof HTMLTableElement)) {
			throw new TypeError('Argument table must be a <table>');
		}
		
		this.#options = {
			...TableSorter.DEFAULT_SETTINGS,
			...options,
		};
		
		
		// Guarantees the values are unique and not empty
		if(this.#options.operations.length) {
			/** @type {Set<TableSorterOperation>} */
			let operations_set = new Set(this.#options.operations);
			
			// We need to reset the array - and this goes around the `NonEmptyArray` type
			this.#options.operations.length = 0;
			
			// Then we push the *unique* values from the set into the array
			operations_set.forEach((operation) => this.#options.operations.push(operation));
		} else {
			this.#options.operations = TableSorter.DEFAULT_SETTINGS.operations;
		}
		
		
		this.#table = table;
		
		/** @type {HTMLTableSectionElement|null} */
		const tbody = table.querySelector('tbody');
		if(!tbody) {
			throw new TypeError('Invalid table - <tbody> required');
		}
		
		this.#tbody = tbody;
		
		this.#init();
		
		if(this.#options.autoSort) {
			this.#initAutoSort();
		}
	}
	
	
	
	/**
	 * Initializes everything
	 * 
	 * @throws {TypeError} Invalid table - no columns found
	 */
	#init() {
		/** @type {HTMLTableCellElement[]} */
		let columns = Array.from(this.#options.columns);
		if(!columns.length) {
			/** @type {Array<Element>} Try to find all sortable columns */
			columns = Array.from(this.#table.querySelectorAll('thead td, th'));
		} else {
			columns.filter((column) => column instanceof HTMLTableCellElement);
		}
		
		if(!columns.length) {
			throw new TypeError('Invalid table - no columns found');
		}
		
		for(const column of columns) {
			if (
				!(column.parentElement instanceof HTMLTableRowElement)
				|| this.#header_rows.has(column.parentElement)
			) {
				continue;
			}
			
			this.#header_rows.add(column.parentElement);
		}
		
		
		for(const row of Array.from(this.#tbody.children || [])) {
			if(!(row instanceof HTMLTableRowElement) || this.#header_rows.has(row)) {
				continue;
			}
			
			this.#rows.push(row);
		}
		
		
		this.#rows.forEach((value, key, _) => this.#rows_original_pos.set(value, key));
		
		
		for (let column of columns) {
			if(!column.getAttribute(TableSorter.SORT_ATTR)) {
				column.setAttribute(TableSorter.SORT_ATTR, this.#options.defaultSortType);
			}
			
			if(this.#options.button) {
				const button = this.#createButton(this.#options.button);
				
				column.appendChild(button);
				
				
				/**
				 * Handles a click event to sort stuff
				 * 
				 * @param {Event} e Event to be handled
				 * 
				 * @returns {void}
				 */
				const handler = (e) => {
					if(
						!e.isTrusted
						|| !(e.currentTarget instanceof Element)
						|| !(e.currentTarget.parentElement instanceof HTMLTableCellElement)
						|| !(e.currentTarget.parentElement.parentElement instanceof HTMLTableRowElement)
						|| !this.#header_rows.has(e.currentTarget.parentElement.parentElement)
						|| !this.#column_buttons.has(e.currentTarget.parentElement)
						|| this.#column_buttons.get(e.currentTarget.parentElement) !== e.currentTarget
					) {
						return;
					}
					
					let column = e.currentTarget.parentElement;
					this.#sortClickHandler(column);
					
					if(this.#options.button) {
						this.#updateButton(e.currentTarget, this.#sort_status, this.#options.button);
					}
				};
				
				button.addEventListener('click', handler);
				this.#click_handlers.set(button, handler);
				this.#column_buttons.set(column, button);
			} else {
				/**
				 * Handles a click event to sort stuff
				 * 
				 * @param {Event} e Event to be handled
				 * 
				 * @returns {void}
				 */
				const handler = (e) => {
					if(
						!e.isTrusted
						|| !(e.currentTarget instanceof HTMLTableCellElement)
						|| !(e.currentTarget.parentElement instanceof HTMLTableRowElement)
						|| !this.#header_rows.has(e.currentTarget.parentElement)
					) {
						return;
					}
					
					this.#sortClickHandler(e.currentTarget);
				};
				
				// The whole cell is clickable
				column.addEventListener('click', handler);
				this.#click_handlers.set(column, handler);
			}
			
			// Make sure all the sorting columns have the attribute
			column.setAttribute(TableSorter.SORTED_ATTR, TableSorter.SORT_NONE);
			
			this.#columns.push(column);
		}
		
		
		if(!this.#columns.length) {
			this.destroy();
			
			throw new TypeError('Invalid table - no columns found');
		}
	}
	
	
	
	/**
	 * Handles the `autoSort` property.
	 * 
	 * @returns {void}
	 */
	#initAutoSort() {
		let autoSort = this.#options.autoSort;
		
		if(!autoSort) {
			return;
		}
		
		/** @type {HTMLTableCellElement|null|undefined} */
		let column = typeof autoSort.column === 'number'
			? this.#columns[autoSort.column]
			: autoSort.column;
		
		if(!column || !this.#columns.includes(column)) {
			console.warn(
				'Invalid \x1B[3m\x1B[47m\x1b[30m%s\x1B[0m value: %O - not an header',
				'autoSort.column',
				column
			);
			
			return;
		}
		
		
		let index = this.#options.operations.indexOf(autoSort.direction);
		
		let operation = this.#options.operations[index === -1 ? 0 : index];
		
		if(!operation) {
			console.warn(
				'Invalid \x1B[3m\x1B[47m\x1b[30m%s\x1B[0m value: %O - not present in \x1B[3m\x1B[47m\x1b[30m%s\x1B[0m list',
				'autoSort.direction',
				autoSort.direction,
				'operations'
			);
			
			return;
		}
		
		// If it wasn't found, then use the first value
		this.#doSortByColumn(column, operation);
		
		
		// Update the button, if needed
		if(!this.#options.button) {
			return;
		}
		
		let button = this.#column_buttons.get(column);
		
		if(!button) {
			return;
		}
		
		this.#updateButton(button, this.#sort_status, this.#options.button);
	}
	
	
	//#endregion
	
	
	
	//#region Sort handling
	
	
	/**
	 * Handles sorting the rows based on the column
	 * 
	 * @param {HTMLTableCellElement} column
	 * 
	 * @returns {void}
	 */
	#sortClickHandler(column) {
		if(column !== this.#sort_column) {
			if(this.#options.button && this.#sort_column) {
				// Reset this column's attribute to `none`
				this.#sort_column.setAttribute(TableSorter.SORTED_ATTR, TableSorter.SORT_NONE);
				
				let button = this.#column_buttons.get(this.#sort_column);
				
				// We must reset the button, if there's one to reset
				if(button) {
					this.#updateButton(
						button,
						TableSorter.SORT_NONE,
						this.#options.button
					);
				}
			}
			
			
			this.#sort_column = column;
			
			// If you set to 0, will always skip the first operation
			this.#sort_op_index = -1;
			
			this.#sort_status = TableSorter.SORT_STATE_RE_SORT;
		}
		
		const sort_op = this.#getNextSortOperation();
		
		/*
			If there's no change, do nothing at all.
			This prevents re-sorting for something like this:
			['asc']
			
			That will always sort `'asc'` on click.
			But once it is sorted, no need to re-sort.
		*/
		if(this.#sort_status === sort_op.operation) {
			/*
				... But we still need to update the index
				Taking the following array as a contrived example:
				['none', 'asc', 'none', 'none', 'asc']
				
				If the index isn't updated, it will be stuck in `2`, instead of going to `3` and then `4`
			*/
			this.#sort_op_index = sort_op.index;
			
			return;
		}
		
		this.#doSortByColumn(column, sort_op.operation);
	}
	
	
	
	/**
	 * Sorts the rows in the table, ignoring header rows inside the `<tbody>`.\
	 * This will also automatically apply the sorting to the table.
	 * 
	 * The values will be sorted according to the returned number:
	 * | Value | Meaning         |
	 * |:-----:|-----------------|
	 * |  `1`  | `a` comes first |
	 * |  `0`  | No change       |
	 * | `-1`  | `b` comes first |
	 * 
	 * @param {((a: HTMLTableRowElement, b: HTMLTableRowElement) => number)|null} [sort_fn=null] The function that will do the sorting, `null` for the original order
	 * 
	 * @returns {void}
	 * 
	 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
	 */
	#sortRows(sort_fn = null) {
		if (sort_fn === null) {
			sort_fn = this.#SORT_FN_ORIGINAL;
		}
		
		this.#rows.forEach((row) => this.#tbody.removeChild(row));
		
		// If I don't bind, the `this.#SORT_FN_ORIGINAL` won't have a `this`...
		this.#rows = this.#rows.sort(sort_fn.bind(this));
		
		this.#rows.forEach((row) => this.#tbody.appendChild(row));
	}
	
	
	
	/**
	 * Performs the sorting
	 * 
	 * @param {HTMLTableCellElement} column
	 * @param {TableSorterOperation} operation
	 * 
	 * @returns {void}
	 */
	#doSortByColumn(column, operation) {
		let index;
		
		const index_attr = column.getAttribute(TableSorter.SORT_INDEX_ATTR);
		
		if(index_attr && /^\d+$/.test(index_attr)) {
			index = Number.parseInt(index_attr, 10);
		} else {
			index = Array.from(column.parentElement?.children ?? []).indexOf(column);
		}
		
		
		if(index === -1) {
			// Should be impossible
			return;
		}
		
		
		/** @type {any} */ // <-- Set as any to avoid: Type 'string' is not assignable to type 'TableSorterTypes | "true" | "false"'. ts(2322)
		let sort_method = column.getAttribute(TableSorter.SORT_ATTR);
		
		/** @type {TableSorterType} */
		let new_sort_method = this.#options.defaultSortType;
		
		if(sort_method === 'false') {
			return;
		} else if(sort_method !== 'true') {
			/**
			 * The default behaviour of `'true'` is to get `this.#options.defaultSortType`.
			 * Since this is already done as it is the default value, we don't need to run all this code.
			 * This code is meant to be executed for any other value besides `'true'` and `'false'`.
			 */
			
			if(sort_method && TableSorter.SORT_ATTR_VALID_VALUES.includes(sort_method)) {
				/**
				 * The value is guaranteed to be a valid `TableSorterType`
				 * This is due to the check above.
				 * The value checked is of type `TableSorterType[]`
				 */
				
				new_sort_method = sort_method;
			} else if(this.#options.replacementSortType) {
				new_sort_method = this.#options.replacementSortType;
			} else {
				console.warn(
					'Invalid sort type \x1B[3m\x1B[47m\x1b[30m%s\x1B[0m for %o - valid values: %s',
					sort_method,
					column,
					TableSorter.SORT_ATTR_VALID_VALUES.join(', ')
				);
				
				// cspell:ignore mreplacement
				console.info('You can change the setting \x1B[3m\x1B[47m\x1b[30mreplacementSortType\x1B[0m to a valid value');
				
				return;
			}
		}
		
		
		if(operation === TableSorter.SORT_NONE) {
			this.#sort_column = null;
			this.#sortRows();
		} else {
			/** @type {(value_a: string, value_b: string) => number} Sort function to be used */
			const sort_function = this.#options.customSortFn
				?? TableSorter.SORT_FUNCTIONS[new_sort_method];
			
			
			this.#sortRows((tr_a, tr_b) => {
				const cell_a = tr_a.children[index];
				const cell_b = tr_b.children[index];
				
				if(
					!(cell_a instanceof HTMLTableCellElement)
					|| !(cell_b instanceof HTMLTableCellElement)
				) {
					return 0;
				}
				
				const value_a = this.#cellGetValue(cell_a);
				const value_b = this.#cellGetValue(cell_b);
				
				let sort_value = sort_function(value_a, value_b);
				
				if(operation === TableSorter.SORT_DESC) {
					/*
						Inverts the sorting value.
						
						Let V be the original value:
						|  V | ~V | 1+~V |
						|:--:|:--:|:----:|
						|  1 | -2 |  -1  |
						|  0 | -1 |   0  |
						| -1 |  0 |   1  |
						
						As you can see, 1 and -1 are inverted.
					 */
					sort_value = 1 + ~sort_value;
				}
				
				return sort_value;
			});
		}
		
		this.#sort_op_index = this.#options.operations.indexOf(operation);
		this.#sort_status = operation;
		this.#sort_column = column;
		
		column.setAttribute(TableSorter.SORTED_ATTR, operation);
	}
	
	
	//#endregion
	
	
	
	//#region Button handling
	
	
	/**
	 * Creates a button to be clickable.
	 * 
	 * @param {TableSorterSettingsButton} settings The button settings
	 * 
	 * @returns {HTMLElementTagNameMap[TableSorterSettingsButton['tag']]}
	 */
	#createButton(settings) {
		let button = document.createElement(settings.tag);
		
		let status = TableSorter.SORT_NONE;
		
		
		if(button instanceof HTMLAnchorElement) {
			// Prevent scrolling to the top of the page
			button.href = 'javascript:void(0);';
		} else if (button instanceof HTMLButtonElement) {
			// Prevent submitting forms and stuff
			button.type = 'button';
		}
		
		
		if(settings.className) {
			button.className = settings.className;
		}
		
		if(settings.text) {
			button.textContent = settings.text[status];
		} else if(settings.icon) {
			let icon = document.createElement(settings.icon.tag);
			
			// We don't want screen readers trying to parse the icons
			icon.setAttribute('aria-hidden', 'true');
			
			icon.className = settings.icon.baseClass ?? '';
			icon.classList.add(...settings.icon[status].split(' '));
			
			button.appendChild(icon);
		}
		
		if(settings.title) {
			// We need to set the title for the button
			if(typeof settings.title === 'string') {
				button.setAttribute('title', settings.title);
			} else {
				button.setAttribute('title', settings.title[status]);
			}
		}
		
		return button;
	}
	
	
	
	/**
	 * Creates a button to be clickable.
	 * 
	 * @param {Element} button The button to update
	 * @param {TableSorterState} status The new status
	 * @param {TableSorterSettingsButton} settings The button settings
	 * 
	 * @returns {void}
	 */
	#updateButton(button, status, settings) {
		/** @type {TableSorterOperation} */
		let new_status = TableSorter.SORT_NONE;
		
		if(status === TableSorter.SORT_ASC || status === TableSorter.SORT_DESC) {
			new_status = status;
		}
		
		if(settings.text) {
			button.textContent = settings.text[new_status] ?? '';
		} else if(settings.icon) {
			let icon = button.querySelector(settings.icon.tag);
			
			if(!icon) {
				return;
			}
			
			icon.className = settings.icon.baseClass ?? '';
			
			if(typeof settings.icon[new_status] === 'string') {
				icon.classList.add(...settings.icon[new_status].split(' '));
			}
		}
		
		if(settings.title) {
			// We need to set the title for the button
			if(typeof settings.title === 'string') {
				button.setAttribute('title', settings.title);
			} else if(typeof settings.title[new_status] === 'string') {
				button.setAttribute('title', settings.title[new_status]);
			}
		}
	}
	
	
	//#endregion
	
	
	
	//#region Internal Utilities
	
	
	/**
	 * Gets the value of a cell, either from the text or the attribute.
	 * 
	 * @see {TableSorter.SORT_VALUE_ATTR}
	 * 
	 * @param {HTMLTableCellElement} cell The cell to get the value from
	 * 
	 * @returns {string}
	 */
	#cellGetValue(cell) {
		if(this.#options.customGetValueFn) {
			try {
				const value = this.#options.customGetValueFn(cell);
				
				if(typeof value === 'string') {
					return value;
				}
			} catch(e) {
				console.error(e);
			}
		}
		
		return cell.getAttribute(TableSorter.SORT_VALUE_ATTR) || cell.textContent;
	}
	
	
	
	/**
	 * Parses a string representation of a number into a `Number`.  
	 * Supports the following formats:
	 * - No thousands separator - E.g: `123456.789`
	 * - Comma for thousands separator - E.g: `123,456.789`
	 * - Period for thousands separator - E.g.: `123.456,789`
	 * 
	 * You can also use `<` or `>` as a prefix, and `%` as a suffix.  
	 * When `<` is used, the number will be a tiny bit smaller.  
	 * When `>` is used, the number will be a tiny bit bigger.  
	 * That tiny bit is the smallest possible value - the `Number.EPSILON`.
	 * 
	 * @param {string} number The text to parse
	 * 
	 * @return {number} `NaN` if invalid
	 */
	static parseNumber(number) {
		/** @type {number} */
		let value = NaN;
		
		/** @type {string} A symbol that will be handled later on */
		let symbol = number.replace(/[^<>]+/g, '');
		
		if(symbol.length && symbol[0]) {
			number = number.replace(symbol[0], '');
		}
		
		number = number
			// If it is a percentage, remove the %
			.replace('%', '')
			// We don't need whitespace
			.trim();
		
		
		if(/^\d+(?:\.\d+)?$/.test(number)) {
			// No thousands separator - E.g: 123456.789
			value = Number.parseFloat(number);
		} else if(/^\d+(?:,\d+)?$/.test(number)) {
			// No thousands separator, comma for decimals - E.g: 123456,789
			value = Number.parseFloat(number.replaceAll(',', '.'));
		} else if(/^(?:\d{1,3},)*\d{3}(?:\.\d+)?$/.test(number)) {
			// Comma for thousands separator - E.g: 123,456.789
			value = Number.parseFloat(number.replaceAll(',', ''));
		} else if(/^(?:\d{1,3}\.)*\d{3}(?:,\d+)?$/.test(number)) {
			// Period for thousands separator - E.g.: 123.456,789
			value = Number.parseFloat(
				number
					.replaceAll('.', '')
					.replaceAll(',', '.')
			);
		}
		
		
		// Handle the symbol
		if(!Number.isNaN(value)) {
			switch(symbol) {
				case '<':
					value -= Number.EPSILON;
					break;
				
				case '>':
					value += Number.EPSILON;
					break;
			}
		}
		
		return value;
	}
	
	
	
	/**
	 * Gets the next sorting operation - **DOES NOT UPDATE ANY VALUES**
	 * 
	 * @returns {{
	 * 	index: number,
	 * 	operation: TableSorterSettings['operations'][0]
	 * }}
	 */
	#getNextSortOperation() {
		let index = this.#sort_op_index + 1;
		
		if(index >= this.#options.operations.length) {
			index = 0;
		}
		
		return {
			index: index,
			operation: this.#options.operations[index] ?? TableSorter.SORT_NONE,
		};
	}
	
	
	//#endregion
	
	
	
	//#region Public Methods
	
	
	/**
	 * Gets the current sort state
	 * 
	 * @returns {TableSorterState}
	 */
	getSortState() {
		return this.#sort_status;
	}
	
	
	
	/**
	 * Destroys the whole work we just did...
	 * 
	 * @param {boolean} [revert_sort=true] Reverts the order to the original
	 * 
	 * @returns {void}
	 */
	destroy(revert_sort = true) {
		if(this.#sort_status === TableSorter.SORT_STATE_DESTROYED) {
			return;
		}
		
		// Remove all event listeners
		for(const [element, handler] of this.#click_handlers.entries()) {
			element.removeEventListener('click', handler);
		}
		
		
		// Remove all buttons
		for(const [column, button] of this.#column_buttons.entries()) {
			column.removeChild(button);
		}
		
		
		// Remove all stay attributes
		for(const column of this.#columns) {
			column.removeAttribute(TableSorter.SORTED_ATTR);
		}
		
		
		if(revert_sort) {
			this.#sortRows();
		}
		
		
		this.#sort_status = TableSorter.SORT_STATE_DESTROYED;
		
		// Clear all the things with elements
		
		this.#options = TableSorter.DEFAULT_SETTINGS;
		
		this.#header_rows.clear();
		this.#rows_original_pos.clear();
		this.#click_handlers.clear();
		this.#column_buttons.clear();
		
		this.#columns = [];
		this.#rows = [];
		
		// Intentionally set these to an invalid valid, to remove any reference to the elements
		//@ts-ignore Type 'null' is not assignable to type 'HTMLTableElement'. ts(2322)
		this.#table = null;
		//@ts-ignore Type 'null' is not assignable to type 'HTMLTableSectionElement'. ts(2322)
		this.#tbody = null;
		
		this.#sort_column = null;
		this.#sort_op_index = -1;
	}
	
	
	//#endregion
}
